// Vendors
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { TxButton, TxButtonMode, TxLoadingText } from 'texkit-ui';

// Components
import { Trans } from '../../Trans';

// Helpers
import { clearAuthSession } from '../../../reducers/auth/authAuthorizeHelpers';
import { buildUrl } from '../../../lib/utmHelpers';

// Selectors
import { 
  selectServerErrorModalOpen, 
  selectUniqueIdentifier,
  selectAppUrl,
  selectUtmParams,
} from '../../../reducers/auth/authSelectors';
import { selectSelectedLanguage } from '../../../reducers/language/languageSelectors';

// Constants
import { texasGovHelpDeskUrl, texasGovHelpDeskUrlEs } from '../../../constants/appUrls';

// Styles
import './server-error-modal.scss';

const ServerErrorModal = () => {
  const isOpen = useSelector(selectServerErrorModalOpen);
  const uniqueIdentifier = useSelector(selectUniqueIdentifier);
  const appUrl = useSelector(selectAppUrl);
  const language = useSelector(selectSelectedLanguage);
  const utmParams = useSelector(selectUtmParams);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const helpDeskUrl = language === 'es' ? texasGovHelpDeskUrlEs : texasGovHelpDeskUrl;

  const handleBackClick = () => {
    if (appUrl && !isSubmitting) {
      setIsSubmitting(true);
      clearAuthSession();
      window.location.href = appUrl;
    }
  };

  const handleOpenHelpDesk = () => {
    window.open(buildUrl(helpDeskUrl, utmParams), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="server-error-modal-backdrop" role="presentation">
      <div
        className="server-error-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="server-error-modal-title"
        aria-describedby="server-error-modal-description"
      >
        <h2 id="server-error-modal-title" className="server-error-modal-title">
          <Trans
            file="auth"
            id="serverErrorTitle"
            fallback="We ran into a problem"
          />
        </h2>

        <p
          id="server-error-modal-description"
          className="server-error-modal-description"
        >
          <Trans
            file="auth"
            id="serverErrorBody"
            fallback="Please try again. If you continue to experience this issue, please contact the "
          />
          <TxButton
            type="button"
            className="server-error-modal-helpdesk-link"
            onClick={handleOpenHelpDesk}
          >
            <Trans
              file="auth"
              id="texasGovHelpDesk"
              fallback="Texas.gov Help Desk"
            />
          </TxButton>
        </p>

        {appUrl && (
          <div className="server-error-modal-actions">
            <TxButton
              className="server-error-modal-back-btn"
              mode={TxButtonMode.Primary}
              onClick={handleBackClick}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <TxLoadingText>
                  <Trans file="auth" id="goBack" fallback="Go back" />
                </TxLoadingText>
              ) : (
                <Trans file="auth" id="goBack" fallback="Go back" />
              )}
            </TxButton>
          </div>
        )}

        {uniqueIdentifier && (
          <p className="server-error-modal-error-id" aria-live="polite">
            <span className="server-error-modal-error-id-label">
              <Trans
                file="auth"
                id="serverErrorIdLabel"
                fallback="Error ID: "
              />
            </span>
            <span className="server-error-modal-error-id-value">
              {uniqueIdentifier}
            </span>
          </p>
        )}
      </div>
    </div>
  );
};

export default ServerErrorModal;
